import axios from "axios";
import { API_URL, getHeaders } from "../config";

/**
 * The learner's own "My Learning Plan" — content individually assigned to them,
 * with completion status derived server-side from existing telemetry.
 * Additive: this never affects the role-based catalog the learner already sees.
 */
export const getMyAssignments = async () => {
  try {
    const { data } = await axios.get(`${API_URL}/api/assignment/me`, {
      headers: getHeaders().headers,
    });
    return data?.data ?? [];
  } catch (error) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      localStorage.removeItem("token");
      window.location.href = "/";
    }
    return [];
  }
};

/**
 * Fetch one assigned item, ready to render.
 *
 * Authorized by the assignment alone, so it returns the content even when the
 * learner's section/department scope wouldn't let them browse to it. This is
 * what makes "assigned" mean "can open it". Returns null if the content is gone
 * or was never assigned to them.
 */
export const openAssignedContent = async (contentType, contentId) => {
  try {
    const { data } = await axios.get(`${API_URL}/api/assignment/open`, {
      params: { contentType, contentId },
      headers: getHeaders().headers,
    });
    return data?.data ?? null;
  } catch (error) {
    if (error?.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/";
    }
    return null;
  }
};

/**
 * Download the completion certificate PDF for any finished assigned content
 * (a learning path, a module, etc.). The server re-verifies BOTH that the
 * content is assigned to the learner and that it's completed before issuing, so
 * this only succeeds for genuinely completed content. Returns an error message
 * string on failure, or null on success.
 */
export const downloadCertificate = async (
  contentType,
  contentId,
  fallbackName = "Certificate"
) => {
  try {
    const res = await axios.get(
      `${API_URL}/api/certificate/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}`,
      {
        headers: getHeaders().headers,
        responseType: "blob",
      }
    );
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `Certificate_${fallbackName}.pdf`.replace(/[^a-z0-9_.]+/gi, "_");
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return null;
  } catch (error) {
    // The error body is a blob (responseType blob); read it back as JSON.
    let message = "Could not download certificate";
    try {
      const text = await error?.response?.data?.text?.();
      if (text) message = JSON.parse(text)?.message || message;
    } catch (_) {}
    return message;
  }
};
